import React, { useState, useRef } from "react";
import { Modal, ModalBody, Row, Col, Input, Label } from "reactstrap";
import { Select, Spin } from "antd";
import { IoCloseSharp } from "react-icons/io5";
import { FiMessageSquare } from "react-icons/fi";
import GeneralButton from "../button/GeneralButton";
import { getAllProducts } from "../../api/pagination";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

const AddReviewModal = ({ modal, toggle, fetchReviews }) => {
    const { all_products } = useSelector((state) => state.products);
    const { user } = useSelector((state) => state.auth);
    
    const [searchProducts, setSearchProducts] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const debounceTimeout = useRef(null);

    // Form state
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [reviewerName, setReviewerName] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const handleSearchProducts = async (searchText) => {
        setSearchQuery(searchText);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (!searchText || searchText.trim().length === 0) {
            setSearchProducts([]);
            setDataLoading(false);
            return;
        }

        setDataLoading(true);

        debounceTimeout.current = setTimeout(() => {
            getAllProducts(1, searchText.trim(), 50, "", (res) => {
                setSearchProducts(res?.data?.data ?? []);
                setDataLoading(false);
            }, "exact");
        }, 500);
    };

    const handleSave = async () => {
        if (!selectedProduct) {
            toast.error("Please select a product to review.");
            return;
        }
        if (!comment.trim()) {
            toast.error("Please enter a review comment.");
            return;
        }

        setIsSaving(true);
        try {
            const reviewData = {
                productId: selectedProduct._id,
                userId: "Anonymous",
                name: reviewerName.trim() || "Anonymous",
                rating: parseInt(rating),
                comment: comment.trim(),
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            };

            await firebase.firestore().collection("Reviews").add(reviewData);
            
            toast.success("Review added successfully!");
            
            // Reset form
            setSelectedProduct(null);
            setRating(5);
            setComment("");
            setReviewerName("");
            
            toggle();
            if (fetchReviews) fetchReviews();
        } catch (error) {
            console.error("Error adding review:", error);
            toast.error("Failed to add review. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={modal} toggle={toggle} centered size="lg" onClosed={() => {
            setSelectedProduct(null);
            setSearchProducts([]);
            setSearchQuery("");
            setRating(5);
            setComment("");
            setReviewerName("");
        }}>
            <ModalBody className="p-0 border-0">
                <div className="d-flex justify-content-between align-items-center p-4 border-bottom bg-light rounded-top">
                    <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                        <FiMessageSquare className="text-primary" /> Add Product Review
                    </h5>
                    <IoCloseSharp size={24} className="text-muted pointer hover-opacity-75" onClick={toggle} style={{ cursor: "pointer" }} />
                </div>

                <div className="p-4">
                    <div className="mb-4">
                        <Label className="fw-bold small text-muted text-uppercase mb-2">Search & Select Product *</Label>
                        <Select
                            size="large"
                            showSearch
                            loading={dataLoading}
                            notFoundContent={dataLoading ? <Spin size="small" /> : "No products found"}
                            placeholder="Start typing SKU or Name..."
                            filterOption={false}
                            onSelect={(value, option) => setSelectedProduct(option.item)}
                            onSearch={(val) => handleSearchProducts(val)}
                            className="w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            options={(searchQuery ? searchProducts : all_products)?.map((item) => ({
                                value: item._id,
                                label: `${item.sku} - ${item.name || item.item_title || "Product"}`,
                                item,
                            }))}
                            value={selectedProduct?._id || undefined}
                        />
                    </div>

                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Label className="fw-bold small text-muted text-uppercase mb-2">Reviewer Name</Label>
                            <Input 
                                type="text" 
                                placeholder="Anonymous"
                                value={reviewerName}
                                onChange={(e) => setReviewerName(e.target.value)}
                                className="px-3 py-2 border rounded-2"
                            />
                        </Col>
                        <Col md={6}>
                            <Label className="fw-bold small text-muted text-uppercase mb-2">Star Rating *</Label>
                            <Input 
                                type="select" 
                                value={rating}
                                onChange={(e) => setRating(e.target.value)}
                                className="px-3 py-2 border rounded-2 cursor-pointer"
                            >
                                <option value="5">5 Stars (Excellent)</option>
                                <option value="4">4 Stars (Good)</option>
                                <option value="3">3 Stars (Average)</option>
                                <option value="2">2 Stars (Poor)</option>
                                <option value="1">1 Star (Terrible)</option>
                            </Input>
                        </Col>
                        <Col md={12}>
                            <Label className="fw-bold small text-muted text-uppercase mb-2">Review Comment *</Label>
                            <Input 
                                type="textarea" 
                                rows={4}
                                placeholder="Type the review comment here..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                className="px-3 py-2 border rounded-2"
                            />
                        </Col>
                    </Row>

                    {/* Actions */}
                    <div className="d-flex justify-content-end gap-2 pt-4 border-top">
                        <GeneralButton
                            title="Cancel"
                            className="bg-white border text-dark px-4 py-2 rounded-2"
                            handleClick={toggle}
                        />
                        <GeneralButton
                            title="Submit Review"
                            className="bg-primary text-white border-0 px-4 py-2 rounded-2 shadow-sm"
                            handleClick={handleSave}
                            disabled={!selectedProduct || !comment.trim() || isSaving}
                            loading={isSaving}
                        />
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default AddReviewModal;
